import Vue, { PropType } from 'vue'
import { saveFreelancerChannelsProfile, saveCustomerChannelsProfile } from '@/api/users'
import { Channel as Channels } from '@/constants/user/channels'
import responseMixin from '@/mixins/responseMixin'
import Channel from '@/models-ts/user/Channel'
import focusableMixin from '@/pages/ProfileSettings/focusableMixin'
import notifiableRequest from '@/utils-ts/notifiableRequest'
import { getSocialLink } from '@/utils-ts/strings'

export default Vue.extend<any, any, any, any>({
  mixins: [focusableMixin, responseMixin],
  props: {
    channels: {
      type: Array as PropType<Array<Channel>>,
      default: () => [],
    },
    // profileType defines which API call and payload structure should be used
    profileType: {
      type: String as PropType<'freelancer' | 'customer'>,
      default: 'freelancer',
    },
    // Active role is required when profileType === 'customer'
    role: Number, // customer_type value
  },
  data() {
    return {
      editMode: false,
      saving: false,
      form: [],
      facebook: '',
      linkedIn: '',
      x: '',
      editableFields: ['contacts'],
      Channels,
    }
  },
  computed: {
    socialValidationRules() {
      // max length is identical for every network now but keep it computed for flexibility
      return 'max:160'
    },
    wasUpdated() {
      return (
        this.facebook !== this.getChannel(Channels.Facebook) ||
        this.linkedIn !== this.getChannel(Channels.LinkedIn) ||
        this.x !== this.getChannel(Channels.Twitter)
      )
    },
    isCustomer(): boolean {
      return this.profileType === 'customer'
    },
  },
  watch: {
    channels: {
      handler() {
        if (this.channels) {
          this.initForm()
        }
      },
      immediate: true,
    },
  },
  methods: {
    getSocialLink,
    getChannel(type: Channels) {
      const channels = this.channels as Array<Channel>
      if (this.isCustomer) {
        return channels.find((ch) => ch.type === type && ch.customerType === this.role)?.value || ''
      }
      return channels.find((ch) => ch.type === type)?.value || ''
    },
    initForm() {
      this.facebook = this.getChannel(Channels.Facebook)
      this.linkedIn = this.getChannel(Channels.LinkedIn)
      this.x = this.getChannel(Channels.Twitter)
    },
    onCancelClick() {
      this.editMode = false
      this.initForm()
    },
    async onFormSubmit() {
      // Validation (vee-validate) is used in template. If validator is not registered for some reason, skip gracefully.
      if (this.$validator && this.$validator.validateAll) {
        const isValid = await this.$validator.validateAll()
        if (!isValid) return
      }
      try {
        this.saving = true
        const savedChannels = await notifiableRequest({
          request: async () => {
            let payload: any[] = []
            if (this.isCustomer) {
              const socialsToUpdate = [Channels.Facebook, Channels.LinkedIn, Channels.Twitter]

              payload = [
                ...this.channels.filter((ch: Channel) => {
                  const isTargetSocial = socialsToUpdate.includes(ch.type as Channels)
                  const isCurrentRole = ch.customerType === this.role
                  return !(isTargetSocial && isCurrentRole)
                }),
                { type: Channels.Facebook, value: this.facebook.trim(), customer_type: this.role },
                { type: Channels.LinkedIn, value: this.linkedIn.trim(), customer_type: this.role },
                { type: Channels.Twitter, value: this.x.trim(), customer_type: this.role },
              ].filter((ch) => ch.value)

              return saveCustomerChannelsProfile(payload)
            }
            // Freelancer
            payload = [
              ...this.channels.filter((ch: Channel) => ![Channels.Facebook, Channels.LinkedIn, Channels.Twitter].includes(ch.type)),
              { type: Channels.Facebook, value: this.facebook.trim() },
              { type: Channels.LinkedIn, value: this.linkedIn.trim() },
              { type: Channels.Twitter, value: this.x.trim() },
            ].filter((ch) => ch.value)
            return saveFreelancerChannelsProfile(payload)
          },
          title: 'Social media',
          successText: 'Your social media accounts have been successfully updated.',
          failureText: 'Error updating your information. Please try again.',
        })
        this.form = savedChannels.map(Channel.fromServer)
        this.$emit('update', this.form)
        this.editMode = false
      } catch (e) {
        console.error(e)
      } finally {
        this.saving = false
      }
    },
  },
})
